import React from 'react';
import { X, AlertCircle } from 'lucide-react';
import { useScreenSize } from '@/hooks';
import { useWagerCheck } from '@/hooks/useProfile';
import styles from './WagerModal.module.css';

/**
 * WagerModal Component
 * Shows wager requirements and eligibility status
 * Displays when user is not eligible for withdrawal
 */
const WagerModal = ({ isOpen, onClose }) => {
  const { isMobile } = useScreenSize();
  const { data: wagerData, isLoading } = useWagerCheck(isOpen);

  if (!isOpen) return null;

  const wagerInfo = wagerData || {};
  const currentWager = wagerInfo.wager || 0;
  const targetWager = wagerInfo.target || 0;
  const totalDeposit = wagerInfo.total_deposit || 0;
  const multiplier = wagerInfo.multiplier || 1;
  const percentage = wagerInfo.percentage || 0;
  const eligible = wagerInfo.eligible || false;
  const remainingWager = Math.max(0, targetWager - currentWager);
  const progressPercentage = targetWager > 0 
    ? Math.min(100, (currentWager / targetWager) * 100) 
    : 0;

  return (
    <div className={styles.modalOverlay} onClick={onClose}>
      <div className={`${styles.modalContainer} ${isMobile ? styles.mobile : ''}`} onClick={(e) => e.stopPropagation()}>
        <div className={styles.header}>
          <h2 className={styles.title}>Wager Requirements</h2>
          <button 
            className={styles.closeButton} 
            onClick={onClose}
            aria-label="Close modal"
          >
            <X size={20} />
          </button>
        </div>

        <div className={styles.content}>
          {isLoading ? (
            <div className={styles.loadingContainer}>
              <div className={styles.loader}>
                <span className={styles.loaderText}>Loading...</span>
              </div>
            </div>
          ) : (
            <>
              {/* Warning Message */}
              <div className={styles.warningBanner}>
                <AlertCircle size={18} className={styles.warningIcon} />
                <div className={styles.warningContent}>
                  <h3 className={styles.warningTitle}>Withdrawal Not Allowed</h3>
                  <p className={styles.warningText}>
                    You need to complete your wager requirements before you can withdraw funds.
                  </p>
                </div>
              </div>

              {/* Wager Information */}
              <div className={styles.wagerSection}>
                <h4 className={styles.sectionTitle}>Wager Progress</h4>
                
                <div className={styles.wagerCards}>
                  <div className={styles.wagerCard}>
                    <p className={styles.wagerLabel}>Total Deposit</p>
                    <p className={styles.wagerValue}>
                      ₹{parseFloat(totalDeposit).toLocaleString('en-IN', { 
                        minimumFractionDigits: 2, 
                        maximumFractionDigits: 2 
                      })}
                    </p>
                  </div>

                  <div className={styles.wagerCard}>
                    <p className={styles.wagerLabel}>Wager Multiplier</p>
                    <p className={styles.wagerValue}>{multiplier}x</p>
                  </div>

                  <div className={styles.wagerCard}>
                    <p className={styles.wagerLabel}>Required Wager</p>
                    <p className={styles.wagerValue}>
                      ₹{parseFloat(targetWager).toLocaleString('en-IN', { 
                        minimumFractionDigits: 2, 
                        maximumFractionDigits: 2 
                      })}
                    </p>
                  </div>

                  <div className={styles.wagerCard}>
                    <p className={styles.wagerLabel}>Current Wager</p>
                    <p className={styles.wagerValue}>
                      ₹{parseFloat(currentWager).toLocaleString('en-IN', { 
                        minimumFractionDigits: 2, 
                        maximumFractionDigits: 2 
                      })}
                    </p>
                  </div>
                </div>

                {/* Progress Bar */}
                <div className={styles.progressSection}>
                  <div className={styles.progressHeader}>
                    <span className={styles.progressLabel}>Progress</span>
                    <span className={styles.progressPercentage}>
                      {progressPercentage.toFixed(2)}%
                    </span>
                  </div>
                  <div className={styles.progressBar}>
                    <div
                      className={styles.progressFill}
                      style={{ width: `${progressPercentage}%` }}
                    ></div>
                  </div>
                  <p className={styles.remainingText}>
                    Remaining: ₹{parseFloat(remainingWager).toLocaleString('en-IN', { 
                      minimumFractionDigits: 2, 
                      maximumFractionDigits: 2 
                    })}
                  </p>
                </div>

                {/* Percentage Info */}
                {percentage > 0 && (
                  <div className={styles.percentageInfo}>
                    <p className={styles.percentageText}>
                      You have wagered {percentage.toFixed(2)}% of the required amount
                    </p>
                  </div>
                )}
              </div>

              {/* Info Message */}
              <div className={styles.infoContainer}>
                <AlertCircle size={18} className={styles.infoIcon} />
                <p className={styles.infoText}>
                  Please continue playing to meet the wager requirements. Once you reach the required wager amount, you will be eligible to withdraw your funds.
                </p>
              </div>
            </>
          )}
        </div>

        <div className={styles.buttonContainer}>
          <button 
            className={`${styles.button} ${styles.closeButton}`}
            onClick={onClose}
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default WagerModal;
